import React from "react";
import { Link } from "react-router-dom";
import stirredLogo from "./stirred_logo.png";
import shotKo1 from "./screenshots/stirred-ko-1.png";
import shotKo2 from "./screenshots/stirred-ko-2.png";
import shotKo3 from "./screenshots/stirred-ko-3.png";
import shotEn1 from "./screenshots/stirred-en-1.png";
import shotEn2 from "./screenshots/stirred-en-2.png";
import shotEn3 from "./screenshots/stirred-en-3.png";
import shotJa1 from "./screenshots/stirred-ja-1.png";
import shotJa2 from "./screenshots/stirred-ja-2.png";
import shotJa3 from "./screenshots/stirred-ja-3.png";
import { trackStoreClick } from "./firebaseAnalytics";

const APP_STORE_URL = "https://apps.apple.com/app/id6792024242";
const GOOGLE_PLAY_URL = "https://play.google.com/store/apps/details?id=net.daydreamfactory.stirred";

type Locale = "ko" | "en" | "ja";

const LOCALE_STORAGE_KEY = "stirred-landing-locale";

const localeLabels: Record<Locale, string> = {
  ko: "KO",
  en: "EN",
  ja: "JA",
};

const shots: Record<Locale, string[]> = {
  ko: [shotKo1, shotKo2, shotKo3],
  en: [shotEn1, shotEn2, shotEn3],
  ja: [shotJa1, shotJa2, shotJa3],
};

const content: Record<
  Locale,
  {
    navTerms: string;
    navPrivacy: string;
    overline: string;
    title: [string, string];
    lead: string;
    appStore: string;
    googlePlay: string;
    platformNote: string;
    philosophyLabel: string;
    philosophy: Array<{ eyebrow: string; title: string; body: string }>;
    previewLabel: string;
    featuresLabel: string;
    features: Array<[string, string]>;
    responsibleNote: string;
    footerHome: string;
  }
> = {
  ko: {
    navTerms: "이용약관",
    navPrivacy: "개인정보 처리방침",
    overline: "iOS · Android · 3개 언어",
    title: ["읽는 레시피 말고,", "만드는 칵테일."],
    lead:
      "칵테일은 읽는 것보다 직접 만들어볼 때 더 잘 기억됩니다. Stirred는 레시피를 찾아보고, 단계별로 만들어보고, 나만의 바를 채워가는 인터랙티브 칵테일 가이드입니다.",
    appStore: "App Store에서 보기",
    googlePlay: "Google Play에서 보기",
    platformNote: "70종 칵테일 도감 · 오프라인 지원 · 광고 제거는 일회성 구매",
    philosophyLabel: "왜 Stirred인가요",
    philosophy: [
      {
        eyebrow: "Learn by making",
        title: "풀면서 익히는 레시피",
        body: "글라스 선택부터 재료, 분량, 기법, 순서, 가니시까지 — 레시피를 단계별로 직접 수행하며 한 잔을 완성합니다. 결과 점수와 복습 포인트로 헷갈린 부분을 바로 확인해요.",
      },
      {
        eyebrow: "Collection",
        title: "70종의 칵테일 도감",
        body: "클래식부터 세계 각지의 지역 대표 칵테일까지. 이름과 재료로 검색하고 베이스, 도수 등 원하는 조건으로 필터링할 수 있습니다.",
      },
      {
        eyebrow: "My bar",
        title: "내 재료로 찾는 오늘의 한 잔",
        body: "집에 있는 술과 재료를 등록하면, 지금 바로 만들 수 있는 칵테일과 한 가지 재료만 부족한 레시피를 알려드립니다.",
      },
      {
        eyebrow: "My recipes",
        title: "나만의 레시피",
        body: "제조법, 글라스, 맛과 향을 칩으로 선택해 간편하게 구성하고, 나만의 칵테일 레시피로 저장해 내 바에서 관리하세요.",
      },
    ],
    previewLabel: "미리보기",
    featuresLabel: "전체 기능",
    features: [
      ["칵테일 도감", "70종 레시피, 사진 중심 카드 그리드"],
      ["검색·필터", "이름·재료 검색, 베이스·도수·난이도 필터"],
      ["상세 레시피", "재료·분량(ml/oz)·글라스·제조법·가니시 정리"],
      ["실전 제조 모드", "잔 수 자동 계산, 바 타이머, 단계별 진행"],
      ["내 바", "보유 재료 등록, 만들 수 있는 칵테일 확인, 구매 목록"],
      ["공유", "칵테일 상세를 전용 이미지와 링크로 공유"],
    ],
    responsibleNote:
      "Stirred는 음주 문화와 칵테일 제조법을 학습하기 위한 앱입니다. 지나친 음주는 건강에 해롭습니다.",
    footerHome: "홈으로",
  },
  en: {
    navTerms: "Terms",
    navPrivacy: "Privacy Policy",
    overline: "iOS · Android · 3 languages",
    title: ["Don't just read recipes,", "make the cocktail."],
    lead:
      "Cocktails are easier to remember when you make them, not just read about them. Stirred is an interactive cocktail guide for discovering recipes, learning each step, and building a bar of your own.",
    appStore: "View on the App Store",
    googlePlay: "View on Google Play",
    platformNote: "70-cocktail collection · Works offline · Ad removal is a one-time purchase",
    philosophyLabel: "Why Stirred",
    philosophy: [
      {
        eyebrow: "Learn by making",
        title: "Recipes you perform, step by step",
        body: "From choosing the glass to ingredients, amounts, technique, order, and garnish — complete each drink hands-on, then review your score and the steps you missed.",
      },
      {
        eyebrow: "Collection",
        title: "A 70-cocktail encyclopedia",
        body: "From the classics to regional signatures around the world. Search by name or ingredient, and filter by base spirit, strength, and more.",
      },
      {
        eyebrow: "My bar",
        title: "Tonight's drink, from your shelf",
        body: "Register the bottles and ingredients you have at home, and Stirred shows what you can make right now — and what's just one ingredient away.",
      },
      {
        eyebrow: "My recipes",
        title: "Recipes of your own",
        body: "Compose your own cocktails with simple chips for method, glass, and flavor, then save them to your bar.",
      },
    ],
    previewLabel: "Preview",
    featuresLabel: "Everything inside",
    features: [
      ["Cocktail encyclopedia", "70 recipes in a photo-first card grid"],
      ["Search & filters", "Search by name or ingredient, filter by base and strength"],
      ["Recipe details", "Ingredients with ml/oz, glass, method, and garnish"],
      ["Practice mode", "Auto-scaled servings, bar timer, step-by-step making"],
      ["My bar", "Track your shelf, see what you can make, build a shopping list"],
      ["Sharing", "Share any cocktail as a dedicated image and link"],
    ],
    responsibleNote:
      "Stirred is an app for learning cocktail culture and technique. Please drink responsibly.",
    footerHome: "Back to home",
  },
  ja: {
    navTerms: "利用規約",
    navPrivacy: "プライバシーポリシー",
    overline: "iOS · Android · 3言語",
    title: ["読むだけのレシピから,", "つくるカクテルへ。"],
    lead:
      "カクテルは、読むだけでなく実際に作ることで、もっと深く覚えられます。Stirred は、レシピを探し、手順に沿って学び、自分だけのバーを育てるインタラクティブなカクテルガイドです。",
    appStore: "App Storeで見る",
    googlePlay: "Google Playで見る",
    platformNote: "70種のカクテル図鑑 · オフライン対応 · 広告削除は買い切り",
    philosophyLabel: "Stirred の特徴",
    philosophy: [
      {
        eyebrow: "Learn by making",
        title: "手を動かして覚えるレシピ",
        body: "グラス選びから材料・分量・技法・手順・ガーニッシュまで、一杯を実際に完成させながら学びます。スコアと復習ポイントで、迷った箇所をすぐ確認できます。",
      },
      {
        eyebrow: "Collection",
        title: "70種のカクテル図鑑",
        body: "クラシックから世界各地のご当地カクテルまで。名前や材料で検索し、ベースや度数などの条件で絞り込めます。",
      },
      {
        eyebrow: "My bar",
        title: "手元の材料から今夜の一杯を",
        body: "家にあるお酒と材料を登録すると、今すぐ作れるカクテルと、あと一つの材料で作れるレシピが分かります。",
      },
      {
        eyebrow: "My recipes",
        title: "自分だけのレシピ",
        body: "技法・グラス・味わいをチップで選んで手軽に構成し、オリジナルレシピとして保存。マイバーでまとめて管理できます。",
      },
    ],
    previewLabel: "プレビュー",
    featuresLabel: "主な機能",
    features: [
      ["カクテル図鑑", "写真中心のカードグリッドで70種のレシピ"],
      ["検索・フィルター", "名前・材料で検索、ベース・度数で絞り込み"],
      ["詳細レシピ", "材料(ml/oz)・グラス・技法・ガーニッシュを整理"],
      ["実践モード", "杯数の自動計算、バータイマー、ステップ進行"],
      ["マイバー", "手持ちの材料を登録し、作れる一杯と買い物リストを確認"],
      ["共有", "カクテルの詳細を専用画像とリンクで共有"],
    ],
    responsibleNote:
      "Stirred はカクテル文化と製法を学ぶためのアプリです。過度な飲酒にはご注意ください。",
    footerHome: "ホームへ",
  },
};

const detectLocale = (): Locale => {
  if (typeof window === "undefined") {
    return "en";
  }

  const stored = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  if (stored === "ko" || stored === "en" || stored === "ja") {
    return stored;
  }

  const browserLocales = window.navigator.languages?.length
    ? window.navigator.languages
    : [window.navigator.language];

  for (const locale of browserLocales) {
    const normalized = locale.toLowerCase();
    if (normalized.startsWith("ko")) return "ko";
    if (normalized.startsWith("ja")) return "ja";
  }

  return "en";
};

const GOLD = "#EAC568";
const GOLD_DEEP = "#D4A94A";
const INK_ON_GOLD = "#0A1428";
const TEXT = "#EDF1FA";
const TEXT_MID = "#9AA3BD";
const TEXT_DIM = "#6A7290";
const CARD_BORDER = "rgba(220, 230, 255, 0.1)";

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background: "linear-gradient(180deg, #151D33 0%, #0F1320 46%, #0E1220 100%)",
    color: TEXT,
  },
  shell: {
    width: "100%",
    maxWidth: 1120,
    margin: "0 auto",
    padding: "28px 20px 64px",
    boxSizing: "border-box",
  },
  nav: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 16,
    marginBottom: 40,
    flexWrap: "wrap",
  },
  navBrand: {
    color: GOLD,
    textDecoration: "none",
    fontSize: 14,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    fontWeight: 800,
  },
  navActions: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
  },
  navLinks: {
    display: "flex",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    fontSize: 14,
  },
  localeSwitch: {
    display: "inline-flex",
    gap: 4,
    padding: 4,
    borderRadius: 999,
    background: "rgba(255, 255, 255, 0.05)",
    border: `1px solid ${CARD_BORDER}`,
  },
  localeButton: {
    minWidth: 42,
    padding: "8px 10px",
    borderRadius: 999,
    border: "none",
    background: "transparent",
    color: TEXT_MID,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
  },
  activeLocaleButton: {
    background: GOLD,
    color: INK_ON_GOLD,
    boxShadow: "0 6px 18px rgba(234, 197, 104, 0.3)",
  },
  hero: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: 36,
    alignItems: "center",
    padding: "8px 0 40px",
  },
  overline: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 14px",
    borderRadius: 999,
    background: "rgba(234, 197, 104, 0.1)",
    border: "1px solid rgba(234, 197, 104, 0.24)",
    color: GOLD,
    fontSize: 12,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  appId: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    margin: "22px 0 16px",
  },
  appLogo: {
    width: 64,
    height: 64,
    borderRadius: 16,
    objectFit: "cover",
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: "0 10px 24px rgba(0, 0, 0, 0.4)",
  },
  appName: {
    margin: 0,
    fontSize: 22,
    fontWeight: 800,
    letterSpacing: -0.3,
    color: TEXT,
  },
  appCategory: {
    margin: "3px 0 0",
    fontSize: 13,
    color: TEXT_DIM,
  },
  title: {
    margin: "6px 0 16px",
    fontSize: "clamp(2.2rem, 5.6vw, 3.9rem)",
    lineHeight: 1.1,
    letterSpacing: "-0.03em",
    fontWeight: 800,
    color: TEXT,
  },
  titleAccent: {
    color: GOLD,
  },
  lead: {
    margin: "0 0 18px",
    fontSize: 16,
    lineHeight: 1.8,
    color: TEXT_MID,
    maxWidth: 560,
  },
  badgeRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 12,
    marginTop: 24,
  },
  primaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
    padding: "14px 24px",
    borderRadius: 999,
    background: "linear-gradient(135deg, #F2D68A 0%, #D9A93F 100%)",
    color: INK_ON_GOLD,
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    boxShadow: "0 14px 30px rgba(234, 197, 104, 0.24)",
    flex: "1 1 180px",
  },
  secondaryButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 50,
    padding: "14px 24px",
    borderRadius: 999,
    background: "rgba(255, 255, 255, 0.06)",
    border: `1px solid ${CARD_BORDER}`,
    color: TEXT,
    textDecoration: "none",
    fontSize: 15,
    fontWeight: 700,
    flex: "1 1 180px",
  },
  note: {
    marginTop: 16,
    fontSize: 13,
    lineHeight: 1.7,
    color: TEXT_DIM,
  },
  heroVisualWrap: {
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  halo: {
    position: "absolute",
    width: 380,
    height: 380,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(234, 197, 104, 0.16) 0%, rgba(234, 197, 104, 0.04) 46%, rgba(234, 197, 104, 0) 74%)",
    filter: "blur(4px)",
  },
  heroShot: {
    position: "relative",
    width: "min(100%, 340px)",
    borderRadius: 28,
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: "0 28px 60px rgba(0, 0, 0, 0.5)",
  },
  sectionLabel: {
    margin: "56px 0 20px",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: 1.2,
    textTransform: "uppercase",
    color: GOLD_DEEP,
  },
  philosophyGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 16,
  },
  philosophyCard: {
    background: "#1E2436",
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 20,
    padding: "22px 22px 20px",
    boxShadow: "0 12px 30px rgba(0, 0, 0, 0.25)",
  },
  cardEyebrow: {
    margin: "0 0 10px",
    fontSize: 11.5,
    textTransform: "uppercase",
    letterSpacing: 1,
    color: GOLD,
    fontWeight: 700,
  },
  cardTitle: {
    margin: "0 0 8px",
    fontSize: 17.5,
    fontWeight: 800,
    color: TEXT,
  },
  cardText: {
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.7,
    color: TEXT_MID,
  },
  shotsRow: {
    display: "flex",
    gap: 18,
    justifyContent: "center",
    flexWrap: "wrap",
    marginTop: 8,
  },
  shotImg: {
    width: "min(100%, 260px)",
    borderRadius: 22,
    border: `1px solid ${CARD_BORDER}`,
    boxShadow: "0 20px 44px rgba(0, 0, 0, 0.45)",
  },
  featureGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
    gap: 12,
  },
  featureCard: {
    background: "rgba(255, 255, 255, 0.04)",
    border: `1px solid ${CARD_BORDER}`,
    borderRadius: 16,
    padding: "16px 18px",
  },
  featureTitle: {
    margin: "0 0 6px",
    fontSize: 14.5,
    fontWeight: 800,
    color: GOLD,
  },
  featureBody: {
    margin: 0,
    fontSize: 13,
    lineHeight: 1.65,
    color: TEXT_MID,
  },
  responsibleNote: {
    marginTop: 40,
    padding: "14px 18px",
    borderRadius: 14,
    background: "rgba(255, 255, 255, 0.03)",
    border: `1px solid ${CARD_BORDER}`,
    fontSize: 12.5,
    lineHeight: 1.7,
    color: TEXT_DIM,
  },
  footer: {
    marginTop: 48,
    paddingTop: 18,
    borderTop: `1px solid ${CARD_BORDER}`,
    display: "flex",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
    alignItems: "center",
    color: TEXT_DIM,
    fontSize: 13,
  },
  footerLink: {
    color: TEXT_MID,
    textDecoration: "none",
    fontWeight: 600,
  },
};

const StirredLanding: React.FC = () => {
  const [locale, setLocale] = React.useState<Locale>("en");

  const handleStoreClick = React.useCallback((storeName: "app_store" | "google_play") => () => {
    trackStoreClick({
      app_name: "stirred",
      store_name: storeName,
      source_page: "stirred_landing",
    });
  }, []);

  React.useEffect(() => {
    setLocale(detectLocale());
  }, []);

  React.useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    }
  }, [locale]);

  const copy = content[locale];
  const localeShots = shots[locale];

  return (
    <main style={styles.page}>
      <div style={styles.shell}>
        <nav style={styles.nav}>
          <Link to="/" style={styles.navBrand}>
            Daydream Factory
          </Link>
          <div style={styles.navActions}>
            <div style={styles.localeSwitch} aria-label="Language switcher">
              {(Object.keys(localeLabels) as Locale[]).map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLocale(option)}
                  style={{
                    ...styles.localeButton,
                    ...(locale === option ? styles.activeLocaleButton : {}),
                  }}
                >
                  {localeLabels[option]}
                </button>
              ))}
            </div>
            <div style={styles.navLinks}>
              <Link to="/stirred/terms" style={styles.footerLink}>
                {copy.navTerms}
              </Link>
              <Link to="/stirred/privacy" style={styles.footerLink}>
                {copy.navPrivacy}
              </Link>
            </div>
          </div>
        </nav>

        <section style={styles.hero}>
          <div>
            <span style={styles.overline}>{copy.overline}</span>

            <div style={styles.appId}>
              <img src={stirredLogo} alt="Stirred app icon" style={styles.appLogo} />
              <div>
                <p style={styles.appName}>Stirred</p>
                <p style={styles.appCategory}>Cocktails · Learning · Home bar</p>
              </div>
            </div>

            <h1 style={styles.title}>
              {copy.title[0]}
              <br />
              <span style={styles.titleAccent}>{copy.title[1]}</span>
            </h1>
            <p style={styles.lead}>{copy.lead}</p>

            <div style={styles.badgeRow}>
              <a
                href={APP_STORE_URL}
                target="_blank"
                rel="noreferrer"
                onClick={handleStoreClick("app_store")}
                style={styles.primaryButton}
              >
                {copy.appStore}
              </a>
              <a
                href={GOOGLE_PLAY_URL}
                target="_blank"
                rel="noreferrer"
                onClick={handleStoreClick("google_play")}
                style={styles.secondaryButton}
              >
                {copy.googlePlay}
              </a>
            </div>

            <p style={styles.note}>{copy.platformNote}</p>
          </div>

          <div style={styles.heroVisualWrap}>
            <div style={styles.halo} />
            <img src={localeShots[0]} alt="Stirred cocktail encyclopedia screen" style={styles.heroShot} />
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.philosophyLabel}</p>
          <div style={styles.philosophyGrid}>
            {copy.philosophy.map((card) => (
              <article key={card.title} style={styles.philosophyCard}>
                <p style={styles.cardEyebrow}>{card.eyebrow}</p>
                <h3 style={styles.cardTitle}>{card.title}</h3>
                <p style={styles.cardText}>{card.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.previewLabel}</p>
          <div style={styles.shotsRow}>
            {localeShots.map((src, index) => (
              <img key={src} src={src} alt={`Stirred screenshot ${index + 1}`} style={styles.shotImg} />
            ))}
          </div>
        </section>

        <section>
          <p style={styles.sectionLabel}>{copy.featuresLabel}</p>
          <div style={styles.featureGrid}>
            {copy.features.map(([title, body]) => (
              <article key={title} style={styles.featureCard}>
                <h3 style={styles.featureTitle}>{title}</h3>
                <p style={styles.featureBody}>{body}</p>
              </article>
            ))}
          </div>

          <p style={styles.responsibleNote}>{copy.responsibleNote}</p>
        </section>

        <footer style={styles.footer}>
          <span>support@daydreamfactory.net</span>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
            <Link to="/stirred/terms" style={styles.footerLink}>
              {copy.navTerms}
            </Link>
            <Link to="/stirred/privacy" style={styles.footerLink}>
              {copy.navPrivacy}
            </Link>
            <Link to="/" style={styles.footerLink}>
              {copy.footerHome}
            </Link>
          </div>
        </footer>
      </div>
    </main>
  );
};

export default StirredLanding;
